'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/lib/store';
import { tmdb } from '@/lib/tmdb';
import { PlaylistService, Playlist, PlaylistItem, playlistShareUrl, playlistEmbedCode } from '@/lib/playlists';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ListVideo,
  Plus,
  Search,
  Trash2,
  Share2,
  Code2,
  ArrowLeft,
  Film,
  Tv,
  ExternalLink,
  Globe,
  Lock,
  X,
} from 'lucide-react';

// Playlist manager panel — lives inside the dashboard's Playlists tab.

export function PlaylistsPanel() {
  const { user, userProfile } = useAuth();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [selected, setSelected] = useState<Playlist | null>(null);

  // Search-to-add state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchAreaRef = useRef<HTMLDivElement>(null);
  const { watchlist } = useStore();

  // The dropdown only closes on an OUTSIDE click or Escape — clicking results
  // inside must never close it, so several titles can be added in a row
  useEffect(() => {
    if (!searchOpen) return;
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      if (searchAreaRef.current && !searchAreaRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSearchOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchOpen]);

  useEffect(() => {
    if (user) loadPlaylists();
  }, [user]);

  const loadPlaylists = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const lists = await PlaylistService.getUserPlaylists(user.uid);
      setPlaylists(lists);
      // Keep the open playlist in sync after edits
      setSelected(prev => (prev ? lists.find(l => l.id === prev.id) || null : null));
    } catch (error) {
      toast.error('Failed to load playlists');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!user || !newTitle.trim()) return;
    setCreating(true);
    try {
      const ownerName = userProfile?.username || user.email?.split('@')[0] || 'Someone';
      const id = await PlaylistService.createPlaylist(user.uid, ownerName, newTitle.trim());
      setNewTitle('');
      await loadPlaylists();
      const created = await PlaylistService.getPlaylist(id);
      if (created) setSelected(created);
      toast.success('Playlist created');
    } catch (error) {
      toast.error('Failed to create playlist');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (playlist: Playlist) => {
    if (!confirm(`Delete "${playlist.title}"? Anyone with the share link will lose access.`)) return;
    try {
      await PlaylistService.deletePlaylist(playlist.id);
      setSelected(null);
      await loadPlaylists();
      toast.success('Playlist deleted');
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  const handleTogglePublic = async (playlist: Playlist) => {
    try {
      await PlaylistService.updatePlaylist(playlist.id, { isPublic: !playlist.isPublic });
      await loadPlaylists();
      toast.success(playlist.isPublic ? 'Playlist is now private' : 'Playlist is now shareable');
    } catch (error) {
      toast.error('Failed to update playlist');
    }
  };

  const handleCopyLink = (playlist: Playlist) => {
    navigator.clipboard.writeText(playlistShareUrl(playlist.id));
    toast.success('Share link copied');
  };

  const handleCopyEmbed = (playlist: Playlist) => {
    navigator.clipboard.writeText(playlistEmbedCode(playlist.id));
    toast.success('Embed code copied');
  };

  const runSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const data = await tmdb.searchMulti(q);
      setSearchResults(
        (data.results || [])
          .filter((r: any) => (r.media_type === 'movie' || r.media_type === 'tv') && r.poster_path)
          .slice(0, 12)
      );
    } catch (error) {
      // Search failed silently
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery, runSearch]);

  // Optimistic add: update the open playlist locally (no refetch), keep the
  // search dropdown open and focused so several titles can be added in a row
  const handleAddItem = async (result: any) => {
    if (!selected) return;
    const before = selected;

    // Watchlist entries don't carry a release date — look the year up so
    // playlist items always show full metadata
    let year: string | null = (result.release_date || result.first_air_date || '').slice(0, 4) || null;
    if (!year) {
      try {
        const details: any = result.media_type === 'movie'
          ? await tmdb.getMovie(result.id)
          : await tmdb.getShow(result.id);
        year = ((details.release_date || details.first_air_date || '') as string).slice(0, 4) || null;
      } catch (error) {
        // Year stays unknown
      }
    }

    const newItem: PlaylistItem = {
      id: result.id,
      type: result.media_type,
      title: result.title || result.name || 'Untitled',
      posterPath: result.poster_path,
      year,
      addedAt: new Date().toISOString(),
    };
    const updated = { ...selected, items: [...selected.items, newItem] };
    setSelected(updated);
    setPlaylists(prev => prev.map(p => (p.id === updated.id ? updated : p)));
    searchInputRef.current?.focus();

    try {
      await PlaylistService.updatePlaylist(selected.id, { items: updated.items });
    } catch (error) {
      // Roll back on failure
      setSelected(before);
      setPlaylists(prev => prev.map(p => (p.id === before.id ? before : p)));
      toast.error(`Failed to add ${newItem.title}`);
    }
  };

  const handleRemoveItem = async (item: PlaylistItem) => {
    if (!selected) return;
    const before = selected;
    const updated = {
      ...selected,
      items: selected.items.filter(i => !(i.id === item.id && i.type === item.type)),
    };
    setSelected(updated);
    setPlaylists(prev => prev.map(p => (p.id === updated.id ? updated : p)));

    try {
      await PlaylistService.updatePlaylist(selected.id, { items: updated.items });
    } catch (error) {
      setSelected(before);
      setPlaylists(prev => prev.map(p => (p.id === before.id ? before : p)));
      toast.error('Failed to remove item');
    }
  };

  if (!user) return null;

  return !selected ? (
    <div>
      <p className="text-gray-400 text-sm mb-6">
        Build lists of movies and shows, then share them with a link — no account needed to view.
      </p>

      {/* Create */}
      <div className="flex gap-2 mb-8 max-w-md">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="New playlist name..."
          className="h-11 rounded-full bg-white/10 ring-1 ring-white/15 border-0 px-5 text-white placeholder:text-gray-400 backdrop-blur-md"
        />
        <Button onClick={handleCreate} disabled={creating || !newTitle.trim()} className="h-11 rounded-full bg-red-600 hover:bg-red-500 px-5">
          <Plus className="h-4 w-4 mr-1" /> Create
        </Button>
      </div>

      {/* Playlist grid — cinematic cards with poster backdrops */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-2xl" />
          ))}
        </div>
      ) : playlists.length === 0 ? (
        <div className="rounded-2xl bg-gradient-to-br from-gray-900/80 to-black ring-1 ring-white/10 text-center py-16 px-6">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-red-600/15 ring-1 ring-red-500/30 flex items-center justify-center">
            <ListVideo className="h-9 w-9 text-red-500" />
          </div>
          <p className="text-lg font-semibold text-white mb-1">No playlists yet</p>
          <p className="text-sm text-gray-400">Name your first one above — then share it with anyone using a link.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {playlists.map((playlist) => {
            const cover = playlist.items.find(i => i.posterPath)?.posterPath;
            return (
              <button
                key={playlist.id}
                onClick={() => setSelected(playlist)}
                className="group relative overflow-hidden rounded-2xl ring-1 ring-white/10 hover:ring-red-500/60 transition-all duration-300 hover:scale-[1.02] text-left h-48 bg-gray-900"
              >
                {/* Backdrop from the first poster */}
                {cover && (
                  <Image
                    src={tmdb.getImageUrl(cover, 'w500')}
                    alt=""
                    fill
                    sizes="(max-width: 640px) 100vw, 33vw"
                    className="object-cover object-top opacity-35 blur-[2px] scale-110 group-hover:opacity-50 transition-opacity duration-300"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
                <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-red-600/20 blur-[60px] pointer-events-none" />

                <div className="relative h-full p-4 flex flex-col justify-between">
                  {/* Top chips */}
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 rounded-full bg-black/50 ring-1 ring-white/15 px-2.5 py-1 text-[11px] text-gray-200 backdrop-blur-md">
                      {playlist.items.length} title{playlist.items.length !== 1 ? 's' : ''}
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] backdrop-blur-md ring-1 ${
                      playlist.isPublic
                        ? 'bg-green-950/60 text-green-300 ring-green-500/40'
                        : 'bg-black/50 text-gray-400 ring-white/15'
                    }`}>
                      {playlist.isPublic ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                      {playlist.isPublic ? 'Shareable' : 'Private'}
                    </span>
                  </div>

                  {/* Poster fan + title */}
                  <div>
                    <div className="flex -space-x-4 mb-3">
                      {playlist.items.slice(0, 4).map((item, i) => (
                        <div
                          key={`${item.type}-${item.id}`}
                          className="w-12 h-[72px] rounded-lg overflow-hidden ring-2 ring-black relative shadow-xl transition-transform duration-300 group-hover:translate-x-0"
                          style={{ transform: `rotate(${(i - 1.5) * 3}deg)`, zIndex: 4 - i }}
                        >
                          {item.posterPath && (
                            <Image src={tmdb.getImageUrl(item.posterPath, 'w200')} alt={item.title} fill sizes="48px" className="object-cover" />
                          )}
                        </div>
                      ))}
                      {playlist.items.length === 0 && (
                        <div className="w-12 h-[72px] rounded-lg ring-2 ring-black bg-white/5 flex items-center justify-center">
                          <Plus className="h-4 w-4 text-gray-600" />
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-bold tracking-tight text-white group-hover:text-red-400 transition-colors line-clamp-1 drop-shadow-lg">
                      {playlist.title}
                    </h3>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  ) : (
    <div>
      {/* Playlist editor — hero banner */}
      <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> All playlists
      </button>

      {(() => {
        const cover = selected.items.find(i => i.posterPath)?.posterPath;
        return (
          <div className="relative overflow-hidden rounded-2xl ring-1 ring-white/10 mb-8 bg-gray-900">
            {cover && (
              <Image
                src={tmdb.getImageUrl(cover, 'w500')}
                alt=""
                fill
                sizes="100vw"
                className="object-cover object-top opacity-30 blur-[3px] scale-110"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
            <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-red-600/25 blur-[80px] pointer-events-none" />

            <div className="relative p-5 md:p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-black/50 ring-1 ring-white/15 px-2.5 py-1 text-[11px] text-gray-200 backdrop-blur-md">
                  <ListVideo className="h-3 w-3 text-red-400" />
                  {selected.items.length} title{selected.items.length !== 1 ? 's' : ''}
                </span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] backdrop-blur-md ring-1 ${
                  selected.isPublic
                    ? 'bg-green-950/60 text-green-300 ring-green-500/40'
                    : 'bg-black/50 text-gray-400 ring-white/15'
                }`}>
                  {selected.isPublic ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                  {selected.isPublic ? 'Anyone with the link can view' : 'Private'}
                </span>
              </div>

              <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-white drop-shadow-2xl mb-4">
                {selected.title}
              </h2>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="bg-white text-black hover:bg-gray-200 rounded-full font-semibold" onClick={() => handleCopyLink(selected)} disabled={!selected.isPublic}>
                  <Share2 className="h-4 w-4 mr-1.5" /> Copy Link
                </Button>
                <Button size="sm" variant="outline" className="border-0 ring-1 ring-white/20 bg-white/10 text-white hover:bg-white/20 rounded-full backdrop-blur-md" onClick={() => handleCopyEmbed(selected)} disabled={!selected.isPublic}>
                  <Code2 className="h-4 w-4 mr-1.5" /> Embed
                </Button>
                <Button size="sm" variant="outline" className="border-0 ring-1 ring-white/20 bg-white/10 text-white hover:bg-white/20 rounded-full backdrop-blur-md" onClick={() => handleTogglePublic(selected)}>
                  {selected.isPublic ? <><Lock className="h-4 w-4 mr-1.5" /> Make Private</> : <><Globe className="h-4 w-4 mr-1.5" /> Make Shareable</>}
                </Button>
                {selected.isPublic && (
                  <Link
                    href={`/list/${selected.id}`}
                    target="_blank"
                    className="inline-flex items-center rounded-full ring-1 ring-white/20 bg-white/10 text-white hover:bg-white/20 backdrop-blur-md px-3 text-sm font-medium transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 mr-1.5" /> Preview
                  </Link>
                )}
                <Button size="sm" variant="outline" className="border-0 ring-1 ring-red-500/40 bg-red-950/40 text-red-400 hover:bg-red-950/70 rounded-full backdrop-blur-md" onClick={() => handleDelete(selected)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Search to add — your watchlist first, then TMDB results */}
      <div ref={searchAreaRef} className="relative mb-8 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          ref={searchInputRef}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setSearchOpen(true)}
          placeholder="Add from your watchlist or search everything..."
          className="h-11 rounded-full bg-white/10 ring-1 ring-white/15 border-0 pl-11 text-white placeholder:text-gray-400 backdrop-blur-md"
        />
        {searchOpen && (() => {
          const query = searchQuery.trim().toLowerCase();
          const watchlistMatches = watchlist
            .filter(item => !query || item.name.toLowerCase().includes(query))
            .sort((a, b) => a.name.localeCompare(b.name));
          const watchlistKeys = new Set(watchlistMatches.map(i => `${i.type}-${i.id}`));
          const tmdbResults = searchResults.filter(
            (r: any) => !watchlistKeys.has(`${r.media_type}-${r.id}`)
          );

          const renderRow = (key: string, result: any, title: string, posterPath: string | null, subtitle: React.ReactNode) => {
            const inList = selected.items.some(i => i.id === result.id && i.type === result.media_type);
            return (
              <button
                key={key}
                onClick={() => !inList && handleAddItem(result)}
                disabled={inList}
                className="w-full flex items-center gap-3 p-2.5 hover:bg-white/5 transition-colors text-left disabled:opacity-40"
              >
                <div className="w-9 h-[52px] rounded overflow-hidden relative flex-shrink-0 bg-gray-800">
                  {posterPath && (
                    <Image src={tmdb.getImageUrl(posterPath, 'w200')} alt="" fill sizes="36px" className="object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{title}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1">{subtitle}</p>
                </div>
                {inList ? <span className="text-xs text-gray-500">Added</span> : <Plus className="h-4 w-4 text-red-400" />}
              </button>
            );
          };

          return (
            <div className="absolute z-20 top-full mt-2 w-full max-h-96 overflow-y-auto rounded-xl bg-gray-900 ring-1 ring-white/15 shadow-2xl">
              {/* Your watchlist */}
              {watchlistMatches.length > 0 && (
                <>
                  <p className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    From your watchlist
                  </p>
                  {watchlistMatches.map(item =>
                    renderRow(
                      `wl-${item.type}-${item.id}`,
                      { id: item.id, media_type: item.type, name: item.name, poster_path: item.posterPath },
                      item.name,
                      item.posterPath,
                      <>{item.type === 'tv' ? <Tv className="h-3 w-3" /> : <Film className="h-3 w-3" />}{item.type === 'tv' ? 'Show' : 'Movie'}</>
                    )
                  )}
                </>
              )}

              {/* Everything else on TMDB */}
              {query.length >= 2 && (
                <>
                  <p className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 border-t border-white/10">
                    Search results
                  </p>
                  {searching ? (
                    <div className="p-4 text-sm text-gray-400">Searching...</div>
                  ) : tmdbResults.length === 0 ? (
                    <div className="p-4 text-sm text-gray-400">No other results</div>
                  ) : (
                    tmdbResults.map((result: any) =>
                      renderRow(
                        `tmdb-${result.media_type}-${result.id}`,
                        result,
                        result.title || result.name,
                        result.poster_path,
                        <>
                          {result.media_type === 'tv' ? <Tv className="h-3 w-3" /> : <Film className="h-3 w-3" />}
                          {result.media_type === 'tv' ? 'Show' : 'Movie'}
                          {(result.release_date || result.first_air_date) && <> · {(result.release_date || result.first_air_date).slice(0, 4)}</>}
                        </>
                      )
                    )
                  )}
                </>
              )}

              {watchlistMatches.length === 0 && query.length < 2 && (
                <div className="p-4 text-sm text-gray-400">Type to search all movies &amp; shows.</div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Items */}
      {selected.items.length === 0 ? (
        <div className="rounded-2xl bg-gradient-to-br from-gray-900/80 to-black ring-1 ring-white/10 text-center py-16 px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 ring-1 ring-white/10 flex items-center justify-center">
            <Search className="h-7 w-7 text-gray-500" />
          </div>
          <p className="text-gray-300 font-medium mb-1">This playlist is empty</p>
          <p className="text-sm text-gray-500">Use the search above to add your first title.</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 md:gap-4">
          {selected.items.map((item, index) => (
            <div key={`${item.type}-${item.id}`} className="group relative">
              <Link href={`/${item.type}/${item.id}`}>
                <div className="aspect-[2/3] rounded-xl overflow-hidden bg-gray-900 relative ring-1 ring-white/10 group-hover:ring-red-500/60 transition-all duration-300 group-hover:scale-[1.03] shadow-lg">
                  {item.posterPath && (
                    <Image src={tmdb.getImageUrl(item.posterPath, 'w300')} alt={item.title} fill sizes="(max-width: 768px) 33vw, 16vw" className="object-cover" />
                  )}
                  {/* Position badge */}
                  <span className="absolute top-1.5 left-1.5 inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-black/70 ring-1 ring-white/20 px-1.5 text-[11px] font-bold text-white backdrop-blur-md">
                    {index + 1}
                  </span>
                  {/* Hover gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
              <button
                onClick={() => handleRemoveItem(item)}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                title="Remove from playlist"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <p className="mt-1.5 text-xs font-medium text-gray-200 truncate group-hover:text-white transition-colors">{item.title}</p>
              <p className="text-[11px] text-gray-500 flex items-center gap-1">
                {item.type === 'tv' ? <Tv className="h-3 w-3" /> : <Film className="h-3 w-3" />}
                {item.type === 'tv' ? 'Show' : 'Movie'}
                {item.year && <> · {item.year}</>}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
